import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, roleLabel, ROLE_LABELS } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import EntityFormDialog from "@/components/EntityFormDialog";
import { Users, Plus, Search, Loader2, Pencil, Trash2, ShieldCheck, CalendarOff, Archive, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import AbsencesDialog from "@/components/AbsencesDialog";
import ManagerDeviceCompliance from "@/components/ManagerDeviceCompliance";

const EMPTY = {
  email: "", nome: "", cognome: "", role: "read_only",
  hotel_ids: [], password: "", attivo: true, temp_resident_manager: false,
};

export default function Utenti() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [openForm, setOpenForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [absenceUser, setAbsenceUser] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmHard, setConfirmHard] = useState(null);
  const [hardCognome, setHardCognome] = useState("");
  const [resetUser, setResetUser] = useState(null);
  const [resetDateFrom, setResetDateFrom] = useState("");
  const [resetPreview, setResetPreview] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, h] = await Promise.all([
        api.get(`/users?archived=${showArchived ? "true" : "false"}`),
        api.get("/hotels"),
      ]);      setUsers(u.data || []);
      setHotels(h.data || []);
    } catch {
      toast.error("Errore caricamento utenti");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showArchived]);

  const doRestore = async (u) => {
    setBusyId(u.id);
    try { await api.post(`/users/${u.id}/restore`); toast.success(`${u.nome} ${u.cognome} ripristinato`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };
  const doHardDelete = async () => {
    if (!confirmHard) return;
    setBusyId(confirmHard.id);
    try {
      const { data } = await api.delete(`/users/${confirmHard.id}/hard`);
      const c = data.cascade || {};
      const removed = [
        c.user_absences > 0 ? `${c.user_absences} assenze` : null,
        c.notifications > 0 ? `${c.notifications} notifiche` : null,
        c["hotels.resident_manager_id"] > 0 || c["hotels.capo_area_id"] > 0 ? "hotel sganciati" : null,
        c.daily_checks_marked > 0 ? `${c.daily_checks_marked} task marcate` : null,
      ].filter(Boolean).join(" · ");
      toast.success(`${confirmHard.nome} ${confirmHard.cognome} eliminato definitivamente${removed ? " · " + removed : ""}`);
      setConfirmHard(null); setHardCognome("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const openReset = async (u) => {
    setResetUser(u); setResetDateFrom(""); setResetPreview(null);
    try {
      const { data } = await api.get(`/users/${u.id}/full-reset/preview`);
      setResetPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const refreshResetPreview = async (date_iso) => {
    if (!resetUser) return;
    try {
      const { data } = await api.get(`/users/${resetUser.id}/full-reset/preview${date_iso ? `?data_inizio=${date_iso}` : ""}`);
      setResetPreview(data);
    } catch { /* noop */ }
  };
  const doFullReset = async () => {
    if (!resetUser) return;
    setBusyId(resetUser.id);
    try {
      const { data } = await api.post(`/users/${resetUser.id}/full-reset`, resetDateFrom ? { data_inizio: resetDateFrom } : {});
      toast.success(`${data.reset || 0} task resettate per ${resetUser.nome} ${resetUser.cognome}`);
      setResetUser(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (q) {
        const s = q.toLowerCase();
        if (!`${u.nome} ${u.cognome} ${u.email}`.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [users, q, roleFilter]);

  const openCreate = () => { setEditId(null); setForm(EMPTY); setOpenForm(true); };
  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ email: u.email, nome: u.nome, cognome: u.cognome, role: u.role, hotel_ids: u.hotel_ids || [], password: "", attivo: u.attivo, temp_resident_manager: !!u.temp_resident_manager });
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${editId}`, payload);
        toast.success("Utente aggiornato");
      } else {
        if (!form.password || form.password.length < 6) {
          toast.error("Password minimo 6 caratteri");
          setSaving(false);
          return;
        }
        await api.post("/users", form);
        toast.success("Utente creato");
      }
      setOpenForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("Utente disattivato");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const hotelName = (id) => hotels.find((h) => h.id === id)?.nome || id;

  return (
    <div className="space-y-6 ih-anim-in" data-testid="utenti-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-2">
            <Users className="h-3 w-3" /> Gestione utenti
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">
            Team <span className="font-medium">IH</span>
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">{loading ? "Caricamento…" : `${filtered.length} utenti`}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-users" className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${showArchived ? "bg-rose-500 text-white" : "border border-border hover:bg-accent"}`}>
            <Archive className="h-4 w-4" /> {showArchived ? "Mostra attivi" : "Disattivati"}
          </button>
          {!showArchived && (
            <Button data-testid="new-user-btn" onClick={openCreate} className="rounded-xl">
              <Plus className="h-4 w-4 mr-1.5" /> Nuovo utente
            </Button>
          )}
        </div>
      </div>

      <div className="ih-glass rounded-2xl p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, email…" className="pl-9 h-10 bg-background/60" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-[260px] h-10" data-testid="filter-role">
            <SelectValue placeholder="Ruolo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i ruoli</SelectItem>
            {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="ih-glass rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border/60">
              <TableHead>Utente</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Ruolo</TableHead>
              <TableHead>Hotel</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Caricamento…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Nessun utente.</TableCell></TableRow>
            ) : (
              filtered.map((u) => (
                <TableRow key={u.id} className="border-border/40 hover:bg-accent/30" data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-lavender-200 to-lavender-400 text-white flex items-center justify-center text-xs font-semibold">
                        {(u.nome?.[0] || "") + (u.cognome?.[0] || "")}
                      </div>
                      <span className="font-medium">{u.nome} {u.cognome}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell><span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-accent/60 text-xs"><ShieldCheck className="h-3 w-3" /> {roleLabel(u.role)}</span>
                    {u.role === "front_office_manager" && u.temp_resident_manager && (
                      <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 text-[10px] font-medium" data-testid={`temp-rm-badge-${u.id}`}>Temp RM</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.hotel_ids?.length || 0}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.attivo ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-muted text-muted-foreground"}`}>
                      {u.attivo ? "Attivo" : "Disattivato"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {!showArchived && u.role === "resident_manager" && u.id !== me?.id && (
                      <Button data-testid={`absences-user-${u.id}`} size="sm" variant="ghost" onClick={() => setAbsenceUser(u)} title="Assenze">
                        <CalendarOff className="h-3.5 w-3.5 text-lavender-600" />
                      </Button>
                    )}
                    {!showArchived && u.id !== me?.id && (
                      <Button data-testid={`reset-user-${u.id}`} size="sm" variant="ghost" onClick={() => openReset(u)} title="Reset performance utente">
                        <RotateCcw className="h-3.5 w-3.5 text-amber-600" />
                      </Button>
                    )}
                    {!showArchived && (
                      <Button data-testid={`edit-user-${u.id}`} size="sm" variant="ghost" onClick={() => openEdit(u)}><Pencil className="h-3.5 w-3.5" /></Button>
                    )}
                    {!showArchived && u.id !== me?.id && (
                      <Button data-testid={`delete-user-${u.id}`} size="sm" variant="ghost" onClick={() => setDeleteId(u.id)} title="Disattiva">
                        <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                      </Button>
                    )}
                    {showArchived && (
                      <>
                        <Button data-testid={`restore-user-${u.id}`} size="sm" variant="ghost" onClick={() => doRestore(u)} disabled={busyId === u.id} title="Ripristina">
                          {busyId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 text-emerald-600" />}
                        </Button>
                        {u.id !== me?.id && (
                          <Button data-testid={`hard-delete-user-${u.id}`} size="sm" variant="ghost" onClick={() => { setHardCognome(""); setConfirmHard(u); }} title="Elimina definitivamente">
                            <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
                          </Button>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Form Dialog */}
      <EntityFormDialog
        open={openForm}
        onOpenChange={setOpenForm}
        title={editId ? "Modifica utente" : "Nuovo utente"}
        description={editId ? "Aggiorna i dati e i permessi dell'utente." : "Crea un nuovo account per il team IH."}
        size="md"
        onSubmit={submit}
        submitLabel={editId ? "Salva" : "Crea utente"}
        saving={saving}
        testId="user-form"
        submitTestId="user-form-submit"
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Nome</Label><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
          <div className="space-y-2"><Label>Cognome</Label><Input required value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} /></div>
        </div>
        <div className="space-y-2"><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div className="space-y-2">
          <Label>Password {editId && <span className="text-muted-foreground text-xs">(lascia vuoto per non cambiare)</span>}</Label>
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editId ? "••••••••" : "Min 6 caratteri"} />
        </div>
        <div className="space-y-2">
          <Label>Ruolo</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Hotel assegnati</Label>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 rounded-lg border border-border/60 bg-background/40">
            {hotels.map((h) => {
              const checked = form.hotel_ids.includes(h.id);
              return (
                <button
                  type="button"
                  key={h.id}
                  onClick={() => setForm((f) => ({ ...f, hotel_ids: checked ? f.hotel_ids.filter((x) => x !== h.id) : [...f.hotel_ids, h.id] }))}
                  className={`px-3 py-1 rounded-full text-xs transition-colors ${checked ? "bg-lavender-200 text-lavender-900" : "bg-background/60 text-muted-foreground hover:bg-accent/60"}`}
                >
                  {h.nome}
                </button>
              );
            })}
          </div>
        </div>
        {form.role === "front_office_manager" && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200/60 bg-amber-50/40 dark:bg-amber-950/20 p-3">
            <input
              type="checkbox"
              id="temp-rm-check"
              checked={!!form.temp_resident_manager}
              onChange={(e) => setForm({ ...form, temp_resident_manager: e.target.checked })}
              className="mt-0.5"
              data-testid="temp-rm-checkbox"
            />
            <label htmlFor="temp-rm-check" className="text-xs cursor-pointer">
              <span className="font-medium">Delega temporanea Resident Manager</span>
              <span className="block text-muted-foreground mt-0.5">
                Il Front Office Manager sarà trattato come RM per assegnazione giornaliera, task RM e KPI. Ruolo primario invariato.
              </span>
            </label>
          </div>
        )}
        {editId && (
          <ManagerDeviceCompliance
            currentUserRole={me?.role}
            targetUserId={editId}
            targetRole={form.role}
          />
        )}
      </EntityFormDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disattivare l'utente?</AlertDialogTitle>
            <AlertDialogDescription>L'utente non potrà più accedere. Potrai riattivarlo modificando il suo profilo.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" onClick={confirmDelete}>Disattiva</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AbsencesDialog user={absenceUser} open={!!absenceUser} onOpenChange={(o) => !o && setAbsenceUser(null)} />

      <Dialog open={!!confirmHard} onOpenChange={(o) => { if (!o) { setConfirmHard(null); setHardCognome(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="user-hard-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" /> Eliminazione definitiva irreversibile
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare definitivamente <strong className="text-foreground">{confirmHard?.nome} {confirmHard?.cognome}</strong> ({confirmHard?.email}). Le sue assenze verranno cancellate; le task storiche e i log audit verranno mantenuti. <strong className="text-rose-600">L'operazione NON è reversibile.</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <label className="text-xs text-muted-foreground">
              Per confermare, digita esattamente il cognome dell'utente: <strong className="text-foreground">{confirmHard?.cognome}</strong>
            </label>
            <Input value={hardCognome} onChange={(e) => setHardCognome(e.target.value)} placeholder={confirmHard?.cognome || ""} autoFocus className="font-mono" data-testid="user-hard-cognome-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmHard(null); setHardCognome(""); }}>Annulla</Button>
            <Button onClick={doHardDelete} disabled={busyId === confirmHard?.id || hardCognome !== (confirmHard?.cognome || "")}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 disabled:cursor-not-allowed" data-testid="user-hard-confirm">
              {busyId === confirmHard?.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) setResetUser(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="user-reset-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-amber-600">
              <RotateCcw className="h-5 w-5" /> Reset performance utente
            </DialogTitle>
            <DialogDescription>
              Marca tutte le task passate di <strong className="text-foreground">{resetUser?.nome} {resetUser?.cognome}</strong> come "reset amministrativo". Da questo momento il calcolo KPI/classifiche/performance riparte da zero. Le task future continueranno a essere assegnate normalmente. <strong>L'utente resta attivo.</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Data inizio (opzionale)</label>
              <Input type="date" value={resetDateFrom} onChange={(e) => { setResetDateFrom(e.target.value); refreshResetPreview(e.target.value); }} data-testid="reset-date-from" />
              <p className="text-[11px] text-muted-foreground">Lascia vuoto per resettare tutte le task storiche.</p>
            </div>
            <div className="rounded-lg bg-amber-50/40 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-900/40 p-3">
              <div className="text-xs text-amber-800 dark:text-amber-200">
                {resetPreview ? (
                  <>Sto per resettare <strong className="text-foreground tabular-nums">{resetPreview.count}</strong> task. Procedere?</>
                ) : (
                  <>Caricamento conteggio…</>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)}>Annulla</Button>
            <Button onClick={doFullReset} disabled={busyId === resetUser?.id || !resetPreview} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="reset-confirm-btn">
              {busyId === resetUser?.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
